import moment from "moment";
import {
  HeaderLogoWithPatientDetail,
  HeaderPrintList,
  HeaderPrintListProps,
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

// Util
import { formatDatetime } from "react-lib/utils/dateUtils";
import { LineStyle } from "pdfmake/interfaces";
import { getCompanyFont } from "react-lib/apps/HISV3/common/CompanyLogoForm";
import FormSymbolImages from "react-lib/apps/common/pdfPrintList/AddSymbolPrintList";

const FONT_SIZE = 14;

const FORM_NAME = "FormAnesRecord";

const COLORS = {
  black: "#000000",
};

const SvgCheckbox = (check?: boolean) => `<svg width="6" height="6">
  <rect x="1" y="5" rx="1" width="6" height="6" style="fill:transparent;stroke:#000000;stroke-width:0.5;fill-opacity:1;stroke-opacity:0.75" />
  ${
    check &&
    `<polygon style="fill:black;stroke-width:1.5;stroke:black;transform:scale(0.055) translate(10 55)" points="191.268,26.967 59.541,158.683 5.615,104.76 0,110.386 59.541,169.92 196.887,32.585"/>`
  }
  </svg>`;

type FormAnesRecordProps = {
  tobacco: string;
  alcohol: string;
  curentMedication: string;
  vitalSign: any;
  bodyTemperature: any;
  painScore: any;
  detail: HeaderPrintListProps["detail"];
  userFullName: string;
  DateRamrk?: string;
  ORRmark?: string;
  PatientLocation?: any;
  IsWardRemark?: string;
  StartAnesTime?: string | null;
  StartOpTime?: string | null;
  EndAnesTime?: string | null;
  PACUDurartion?: string | null;
  AnesthesiaTeam?: string | null;
  PreoperativeDiagnosis?: string | null;
  PostoperativeDiagnosis?: string | null;
  SurgicalTeam?: string | null;
  ProposedOperation?: string | null;
  PerformedOperation?: string | null;
  // CardAnesRecord02UX
  IsIdentified?: boolean;
  IsConsentPage?: boolean;
  ConsentPageRemark?: string | null;
  NPO?: string | null;
  ASAPS?: string | null;
  IsEmergency?: boolean;
  Premedication?: any;
  Status?: string | null;
  Problems?: string | null;
  Position?: string | null;
  Monitors?: string[];
  MonitorsRemark?: string | null;
  Emergency?: string | null;
  IsVerbalCommand?: boolean;
  IsEffecttiveCough?: boolean;
  IsSwallowingGag?: boolean;
  AdequateSpontaneousVentilation?: boolean;
  IsHandGripHeadLift?: boolean;
  IsStableHemodynamic?: boolean;
  IsNoneAfterExtubation?: boolean;
  UpperAirwayObstruction?: boolean;
  Cyanosis?: boolean;
  Wheezing?: boolean;
  Dyspnea?: boolean;
  IsGeneralAnesthesia?: boolean;
  IsUnderMask?: boolean;
  IsLMA?: boolean;
  IsEndotrachealTube?: boolean;
  IsTracheostomyTube?: boolean;
  IsTIVA?: boolean;
  IsRegionalAnesthesia?: boolean;
  IsSpinalBlock?: boolean;
  IsEpiduralBlock?: boolean;
  IsCombinedSpinalAndEpiduralBlock?: boolean;
  IsCaudalblock?: boolean;
  IsBrachialPlexusBlock?: boolean;
  IsOthers?: boolean;
  IsOthersRemark?: string | null;
  IsMAC?: boolean;
  ApproachAndSite?: string | null;
  AgentAndConc?: string | null;
  Bevel?: string | null;
  NeedleNo?: string | null;
  Attempts?: string | null;
  Analgesiclevel?: string | null;
  SkinToEpiduralSubarahnoidDepth?: string | null;
  CatheterNo?: string | null;
  CatheterLengthInEpiduralSpace?: string | null;
  IsCombinedGeneralAndRegionalAnesthesia?: boolean;
  DutTo?: string | null;
  AirwayManagementIsNone?: boolean;
  AirwayManagementAreaIs2?: boolean;
  AirwayManagementAreaDropdown2?: string | null;
  AirwayManagementAreaNo2?: string | null;
  AirwayManagementAreaIs3?: boolean;
  AirwayManagementAreaDropdown3?: string | null;
  AirwayManagementAreaNo3?: string | null;
  AirwayManagementAreaIs4?: boolean;
  AirwayManagementAreaDropdown4?: string | null;
  AirwayManagementAreaNo4?: string | null;
  AirwayManagementAreaIs5?: boolean;
  AirwayManagementAreaDropdown5?: string | null;
  AirwayManagementAreaIs6?: boolean;
  AirwayManagementAreaDropdown6?: string | null;
  AirwayManagementAreaIs1?: string | null;
  AirwayManagementAreaNo1?: string | null;
  IntubationTime?: string | null;
  AirwayManagementAttempts?: string | null;
  LaryngoscopeView?: string | null;
  Adjunct?: string | null;
  AirwayManagementInduction?: string | null;
  AirwayManagementVentilation?: string | null;
  TransferTo?: string | null;
  IsBalanced?: boolean;
  IsInhalation?: boolean;
  IsNitrousOxide?: boolean;
  IsGeneralAnesthesiaOthers?: boolean;
  IsGeneralAnesthesiaOthersRemark?: string;
  IsRegionalAnesthesiaOthers?: boolean;
  IsRegionalAnesthesiaOthersRemark?: string;
  IsMACRemark?: string;
  IsCombined?: boolean;
  IsCombinedRemark?: string;
  premedication?: any;
  arriveStatus?: string;
  triageLevel?: string;
  pregnancyStatue?: string;
  underlyingDisease?: string;
  allergy?: string;
};

const FormAnesRecord = async (props: FormAnesRecordProps) => {
  const currentDate = moment();

  const headerForm = await HeaderPrintList({
    detail: props.detail,
    font: getCompanyFont(),
    form: FORM_NAME,
    formName: "ANES RECORD",
    logoHeight: 38,
    pageMargins: [8, 120, 8, 24],
    displayFields: [
      ["HN [AN]: ", false],
      [true, true],
      [true, true],
      [true, true],
      [false, false],
    ],
    titleContent: [],
  });

  const { font, fonts, fontSizes, images, lineHeights, styles } = headerForm;

  const anestheticTechniqueData = () => {
    const generalAnesthesiaData = [
      props.IsBalanced && "Balanced",
      props.IsInhalation && "Inhalation",
      props.IsTIVA && "TIVA",
      props.IsNitrousOxide && "Nitrous Oxide",
      props.IsGeneralAnesthesiaOthers && `Others : ${props.IsGeneralAnesthesiaOthersRemark}`,
    ].filter(Boolean) as string[];

    const regionalAnesthesiaData = [
      props.IsSpinalBlock && "Spinal",
      props.IsEpiduralBlock && "Epidural",
      props.IsCombinedSpinalAndEpiduralBlock && "CSE",
      props.IsCaudalblock && "Caudal",
      props.IsBrachialPlexusBlock && "Brachial plexus",
      props.IsRegionalAnesthesiaOthers && `Others : ${props.IsRegionalAnesthesiaOthersRemark}`,
    ].filter(Boolean) as string[];

    const macData = props.IsMAC ? ([props.IsMACRemark] as string[]) : [];
    const combinedData = props.IsCombined ? [`Specify : ${props.IsCombinedRemark}`] : [];

    const block = (title: string, data: string[]) => ({
      ul: [
        { text: title, bold: true },
        { margin: [10, 0, 0, 0], ul: data },
      ],
    });

    return [
      {
        margin: [10, 0, 0, 0],
        stack: [
          props.IsGeneralAnesthesia && block("General anesthesia (GA)", generalAnesthesiaData),
          props.IsRegionalAnesthesia && block("Regional anesthesia (RA)", regionalAnesthesiaData),
          props.IsMAC && block("Monitored anesthesia care (MAC)", macData),
          props.IsCombined && block("Combined GA & RA", combinedData),
        ].filter(Boolean),
      },
    ];
  };

  const beforeExtubationData = () => {
    return (
      ([
        props.IsVerbalCommand && "Verbal command",
        props.IsEffecttiveCough && "Effecttive cough",
        props.IsSwallowingGag && "Swallowing gag",
        props.AdequateSpontaneousVentilation && "Adequate spontaneous ventilation",
        props.IsHandGripHeadLift && "Hand grip / Head lift",
        props.IsStableHemodynamic && "Stable hemodynamic",
      ].filter(Boolean) as string[]) || []
    );
  };

  const afterExtubationData = () => {
    const afterExtubation =
      ([
        props.UpperAirwayObstruction && "Upper airway obstruction",
        props.Cyanosis && "Cyanosis",
        props.Wheezing && "Wheezing",
        props.Dyspnea && "Dyspnea",
      ].filter(Boolean) as string[]) || [];

    return props.IsNoneAfterExtubation ? [props.IsNoneAfterExtubation && "None"] : afterExtubation;
  };

  return {
    defaultStyle: {
      font,
      fontSize: fontSizes[FONT_SIZE],
      lineHeight: lineHeights[1],
    },
    pageSize: "A4",
    ...headerForm,
    images: {
      ...images,
      ...FormSymbolImages,
    },
    styles: {
      ...styles,
      fieldKeyTopic: { bold: true, font: fonts.POPPINS_LM, fontSize: fontSizes[16] },
      fieldKey: { bold: true, font: fonts.POPPINS_LM, fontSize: fontSizes[13.25] },
      fieldKeyBolder: { bold: true, font: fonts.POPPINS, fontSize: fontSizes[13.25] },
      fieldNote: {
        font: fonts.POPPINS_LM,
        fontSize: fontSizes[12],
        italics: true,
      },
      fieldValue: { font: fonts.KANIT_LM, fontSize: fontSizes[13.25] },
      miniField: { fontSize: fontSizes[12] },
    },
    content: [
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                margin: [20, 0, 0, 0],
                stack: [
                  {
                    columns: [
                      {
                        text: "Arrival Status : ",
                        bold: true,
                        width: "16%",
                      },
                      {
                        text: `${props.arriveStatus || "-"}`,
                        width: "16%",
                      },
                      {
                        text: "Triage level : ",
                        bold: true,
                        width: "16%",
                      },
                      {
                        text: `${props.triageLevel || "-"}`,
                        width: "16%",
                      },
                      {
                        text: "Pregnancy : ",
                        bold: true,
                        width: "16%",
                      },
                      {
                        text: `${props.pregnancyStatue || "-"}`,
                        width: "16%",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Underlying disease : ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.underlyingDisease || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Allergy : ",
                        bold: true,
                        width: 45,
                      },
                      {
                        text: `${props.allergy || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: [
                          { text: "BW - ", bold: true },
                          { text: `${props.vitalSign?.BodyWeight || "-"} Kg.` },
                        ],
                        width: 65,
                        fontSize: 9,
                        noWrap: true,
                      },
                      {
                        text: [
                          { text: "HT - ", bold: true },
                          { text: `${props.vitalSign?.Height || "-"} cm.` },
                        ],
                        width: 60,
                        fontSize: 9,
                        noWrap: true,
                      },
                      {
                        text: [
                          { text: "BMI - ", bold: true },
                          { text: `${props.vitalSign?.BMI || "-"}` },
                        ],
                        width: 45,
                        fontSize: 9,
                        noWrap: true,
                      },
                      {
                        text: [
                          { text: "T - ", bold: true },
                          { text: `${props.bodyTemperature?.result || "-"} C` },
                        ],
                        width: 45,
                        fontSize: 9,
                        noWrap: true,
                      },
                      {
                        text: [
                          { text: "BP - ", bold: true },
                          { text: `${props.vitalSign?.BP || "-"} mmHg` },
                        ],
                        width: 85,
                        fontSize: 9,
                        noWrap: true,
                      },
                      {
                        text: [
                          { text: "PR - ", bold: true },
                          { text: `${props.vitalSign?.Pulse || "-"}/min` },
                        ],
                        width: 55,
                        fontSize: 9,
                        noWrap: true,
                      },
                      {
                        text: [
                          { text: "RR - ", bold: true },
                          { text: `${props.vitalSign?.RR || "-"}/min` },
                        ],
                        width: 55,
                        fontSize: 9,
                        noWrap: true,
                      },
                      {
                        text: [
                          { text: "O2 - ", bold: true },
                          { text: `${props.vitalSign?.O2SatRA || "-"}` },
                        ],
                        width: 35,
                        fontSize: 9,
                        noWrap: true,
                      },
                      {
                        text: [
                          { text: "Pain score - ", bold: true },
                          { text: `${props.painScore?.result || "-"}` },
                        ],
                        width: 100,
                        fontSize: 9,
                        noWrap: true,
                      },
                    ],
                    columnGap: 5,
                  },
                  {
                    columns: [
                      {
                        text: "Smoking : ",
                        bold: true,
                        width: 50,
                      },
                      {
                        text: props.tobacco || "-",
                        width: 40,
                      },
                      {
                        text: "Alcohol : ",
                        bold: true,
                        width: 50,
                      },
                      {
                        text: props.alcohol || "-",
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Current Medication : ",
                        bold: true,
                        width: 102,
                      },
                      {
                        text: props.curentMedication || "-",
                        width: "*",
                      },
                    ],
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function (i: number, node: any): number {
            return i === node.table.body.length ? 1 : 0;
          },
          vLineWidth: function (): number {
            return 0;
          },
          hLineStyle: function (i: number, node: any): LineStyle | null {
            if (i === node.table.body.length) {
              return { dash: { length: 3, space: 2 } };
            }
            return null;
          },
          paddingBottom: function (i: number, node: any): number {
            if (i === node.table.body.length - 1) {
              return 8;
            }
            return 0;
          },
        },
      },
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                margin: [20, 10, 0, 0],
                stack: [
                  {
                    text: "Anesthetic Technique",
                    style: "fieldKeyTopic",
                  },
                  {
                    stack: anestheticTechniqueData(),
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function (i: number, node: any): number {
            return i === node.table.body.length ? 1 : 0;
          },
          vLineWidth: function (): number {
            return 0;
          },
          hLineStyle: function (i: number, node: any): LineStyle | null {
            if (i === node.table.body.length) {
              return { dash: { length: 3, space: 2 } };
            }
            return null;
          },
          paddingBottom: function (i: number, node: any): number {
            if (i === node.table.body.length - 1) {
              return 8;
            }
            return 0;
          },
        },
      },
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                margin: [20, 10, 0, 0],
                stack: [
                  {
                    columns: [
                      {
                        text: "ASA-PS: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.ASAPS || "-"} `,
                        width: 50,
                      },
                      {
                        columns: [
                          {
                            image: props.IsEmergency ? "checked" : "unchecked",
                            width: 10,
                            margin: [0, 3, 0, 0],
                          },
                          { text: "Emergency", style: "fieldValue" },
                        ],
                        columnGap: 5,
                        width: `auto`,
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Premedication: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        stack: [{ ul: props.premedication || [] }],
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Status: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.Status || "-"} `,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Problems: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.Problems || "-"} `,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Position: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.Position || "-"} `,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Position: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.Position || "-"} `,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Monitors: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${(props.Monitors || []).join(",") || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Other: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.MonitorsRemark || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Emergence: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.Emergency || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Before extubation: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        stack: [{ ul: beforeExtubationData() }],
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "After extubation: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        stack: [{ ul: afterExtubationData() }],
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Approach & Site: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.ApproachAndSite || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Agent & Conc: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.AgentAndConc || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Bevel: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `${props.Bevel || "-"}`,
                        width: 60,
                      },
                      {
                        text: "Needle No.: ",
                        bold: true,
                        width: 70,
                      },
                      {
                        text: `${props.NeedleNo || "-"}`,
                        width: 20,
                      },
                      {
                        text: "G.",
                        bold: true,
                        width: 30,
                      },
                      {
                        text: "Attempt: ",
                        bold: true,
                        width: 60,
                      },
                      {
                        text: `${props.Attempts || "-"}`,
                        width: 70,
                      },
                      {
                        text: "Analgesic level: ",
                        bold: true,
                        width: 90,
                      },
                      {
                        text: `${props.Analgesiclevel || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Skin to epidural /Subarahnoid depth: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        text: `\n${props.SkinToEpiduralSubarahnoidDepth || "-"}`,
                        width: 30,
                      },
                      {
                        text: "\ncm",
                        bold: true,
                        width: 30,
                      },
                      {
                        text: "\nCatheter: ",
                        bold: true,
                        width: 70,
                      },
                      {
                        text: `\n${props.CatheterNo || "-"}`,
                        width: 20,
                      },
                      {
                        text: "\nG.",
                        bold: true,
                        width: 30,
                      },
                      {
                        text: "\nCatheter length in epidural space: ",
                        bold: true,
                        width: 160,
                      },
                      {
                        text: `\n${props.CatheterLengthInEpiduralSpace || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "",
                        width: 100,
                      },
                      {
                        columns: [
                          {
                            image: props.IsCombinedSpinalAndEpiduralBlock ? "checked" : "unchecked",
                            width: 10,
                            margin: [0, 3, 0, 0],
                          },
                          { text: "Combined general and regional anesthesia", style: "fieldValue" },
                        ],
                        columnGap: 5,
                        width: `auto`,
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "",
                        width: 100,
                      },
                      {
                        text: "due to: ",
                        bold: true,
                        width: 60,
                      },
                      {
                        text: `${props.DutTo || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "Airway Management: ",
                        bold: true,
                        width: 100,
                      },
                      {
                        columns: [
                          {
                            image: props.AirwayManagementIsNone ? "checked" : "unchecked",
                            width: 10,
                            margin: [0, 3, 0, 0],
                          },
                          { text: "None", style: "fieldValue" },
                        ],
                        columnGap: 5,
                        width: `auto`,
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "",
                        width: 100,
                      },
                      {
                        text: "Tube: ",
                        bold: true,
                        width: 40,
                      },
                      {
                        text: `${props.AirwayManagementAreaDropdown2 || "-"}`,
                        width: 80,
                      },
                      {
                        text: "No: ",
                        bold: true,
                        width: 60,
                      },
                      {
                        text: `${props.AirwayManagementAreaNo2 || "-"}`,
                        width: 40,
                      },
                      {
                        text: "Cuff/non-cuff: ",
                        bold: true,
                        width: 80,
                      },
                      {
                        text: `${props.AirwayManagementAreaDropdown3 || "-"}`,
                        width: 60,
                      },
                      {
                        text: "Depth (cm): ",
                        bold: true,
                        width: 60,
                      },
                      {
                        text: `${props.AirwayManagementAreaNo3 || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "",
                        width: 100,
                      },
                      {
                        text: "Blade: ",
                        bold: true,
                        width: 40,
                      },
                      {
                        text: `${props.AirwayManagementAreaDropdown4 || "-"}`,
                        width: 80,
                      },
                      {
                        text: "No. (Blade): ",
                        bold: true,
                        width: 60,
                      },
                      {
                        text: `${props.AirwayManagementAreaNo4 || "-"}`,
                        width: 40,
                      },
                      {
                        text: "Oral/Nasal: ",
                        bold: true,
                        width: 80,
                      },
                      {
                        text: `${props.AirwayManagementAreaDropdown5 || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "",
                        width: 100,
                      },
                      {
                        text: "Attempts: ",
                        bold: true,
                        width: 60,
                      },
                      {
                        text: `${props.AirwayManagementAttempts || "-"}`,
                        width: 40,
                      },
                      {
                        text: "Laryngoscope view: ",
                        bold: true,
                        width: 120,
                      },
                      {
                        text: `${props.LaryngoscopeView || "-"}`,
                        width: 80,
                      },
                      {
                        text: "Intubation time: ",
                        bold: true,
                        width: 80,
                      },
                      {
                        text: `${props.IntubationTime || "-"}`,
                        width: 20,
                      },
                      {
                        text: "sec",
                        bold: true,
                        width: "*",
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: "",
                        width: 100,
                      },
                      {
                        text: "Bag-mask ventilation: ",
                        bold: true,
                        width: 120,
                      },
                      {
                        text: `${props.AirwayManagementAreaIs1 || "-"}`,
                        width: 100,
                      },
                      {
                        text: "Note: ",
                        bold: true,
                        width: 40,
                      },
                      {
                        text: `${props.AirwayManagementAreaNo1 || "-"}`,
                        width: "*",
                      },
                    ],
                  },
                ],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function (i: number, node: any): number {
            return 0;
          },
          vLineWidth: function (): number {
            return 0;
          },
          hLineStyle: function (i: number, node: any): LineStyle | null {
            return { dash: { length: 3, space: 2 } };
          },
          paddingBottom: function (i: number, node: any): number {
            return 8;
          },
        },
      },
    ],
    footer: (currentPage: any, pageCount: any) => ({
      stack: [
        {
          table: {
            widths: ["100%"],
            body: [
              [
                {
                  columns: [
                    {
                      text: [
                        "วันเวลาที่พิมพ์เอกสาร ",
                        { text: formatDatetime(currentDate, true) },
                        ` โดย ${props.userFullName}`,
                      ],
                    },
                    { alignment: "right", text: `หน้า ${currentPage}/${pageCount}` },
                  ],
                  margin: [15, 0, 15, 0],
                  style: "miniField",
                  border: [false, true, false, false],
                },
              ],
            ],
          },
          layout: {
            hLineColor: () => "#c0c0c0",
            hLineStyle: () => ({ dash: { length: 1, space: 1 } }),
            hLineWidth: () => 1,
            vLineWidth: () => 0,
          },
        },
      ],
    }),
  };
};

export default FormAnesRecord;
